import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../Css/Navbar.css";
import { FaRegUserCircle } from "react-icons/fa";
import { IoMdHelpCircleOutline } from "react-icons/io";
import { IoVideocamOutline, IoMailOutline, IoSettingsOutline } from "react-icons/io5";
import { MdOutlineChat, MdOutlineInstallMobile, MdOutlinePhone } from "react-icons/md";
import { TbCalendarStats } from "react-icons/tb";
import { TfiAnnouncement } from "react-icons/tfi";
import { FiLogOut, FiGift } from "react-icons/fi";
import { FaBook, FaLinkedin } from "react-icons/fa";
import { IoMdPrint } from "react-icons/io";
import { FaFacebook } from "react-icons/fa6";
import { BsTwitterX } from "react-icons/bs";
import { AiFillInstagram } from "react-icons/ai";
import { IoIosMail } from "react-icons/io";
// Material UI
import {
  Dialog,
  Button,
  IconButton,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Construction } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// feedback
import emailjs from "emailjs-com";
import Logger from "./Logger";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";
import { MovableLiveStatus } from "./LiveOrderStatus/MovableLiveStatus";
import { usePlan } from "../context/PlanContext";
import PlansDialog from "./PlansDialog";

const ZORDIGO_SOCIALS = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61590390916973",
    Icon: FaFacebook,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/zordigoapp/",
    Icon: AiFillInstagram,
  },
  {
    label: "X",
    href: "https://x.com/Zordigoapp",
    Icon: BsTwitterX,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/zordigo-app-612459416/",
    Icon: FaLinkedin,
  },
  {
    label: "Email",
    href: "mailto:info@zordigo.com",
    Icon: IoIosMail,
  },
];

const FAQ_ITEMS = [
  {
    q: "How do I set up drivers?",
    a: "You can add drivers from the 'Drivers' tab. Drivers need to download the driver app named 'Zordigo Drive', to track the Orders and Performance related to Driver.",
  },
  {
    q: "How do I add orders?",
    a: (
      <>
        You can add orders to the system via:
        <ul>
          <li>
            Manual order entry form by using the &apos;Create Order&apos; button
          </li>
          <li>By uploading the &apos;Excel File&apos;</li>
          <li>By uploading &apos;ERP Excel&apos;</li>
        </ul>
        All options are under the &apos;Orders&apos; tab. Click &apos;Create
        Order&apos; for manual entry, or upload an Excel / ERP Excel to add
        multiple orders at once.
      </>
    ),
  },
  {
    q: "How do I add orders using ERP Excel?",
    a: (
      <>
        Upload the ERP Excel with the &apos;Upload ERP Excel&apos; button on the
        Orders page.
        <br />
        <br />
        <b>Note:</b> First configure these in &apos;Order Upload Settings&apos;
        under Settings:
        <ul>
          <li>Order No. Column</li>
          <li>Company Name Column</li>
          <li>Amount Column</li>
          <li>Date Column</li>
          <li>Row No. from which orders are found</li>
        </ul>
        So the system knows where to read the data from.
      </>
    ),
  },
  {
    q: "What do these different order tabs mean?",
    a: (
      <>
        After placing an order, you can view it under &apos;All Orders&apos;.
        There are also separate tabs:
        <ul>
          <li>
            <b>Assigned Orders:</b> Active orders expected to be delivered soon.
          </li>
          <li>
            <b>Completed Orders:</b> Successfully delivered orders.
          </li>
          <li>
            <b>Incomplete Orders:</b> Orders that could not be delivered due to
            external issues.
          </li>
          <li>
            <b>Old Bills:</b> Orders you had before using Zordigo that are not
            yet completed or assigned.
          </li>
          <li>
            <b>Pending Orders:</b> Completed orders with amount still to be
            collected — reassign to a driver for collection.
          </li>
        </ul>
      </>
    ),
  },
  {
    q: "How do I edit or delete drivers?",
    a: (
      <>
        Edit driver info from the Drivers tab using &apos;Edit&apos; in the
        Actions dropdown. You can change name, phone number, and other details,
        or delete a driver from the same menu.
        <br />
        Driver unique ID is their email — you can&apos;t have two drivers with
        the same email.
      </>
    ),
  },
  {
    q: "How do I edit or delete dispatchers?",
    a: (
      <>
        Edit dispatcher info from the Dispatchers tab using &apos;Edit&apos; in
        the Actions dropdown. You can change name, phone number, and other
        details, or delete a dispatcher from the same menu.
        <br />
        Dispatcher unique ID is their email — you can&apos;t have two
        dispatchers with the same email.
      </>
    ),
  },
  {
    q: "How do I edit or delete packers?",
    a: (
      <>
        Edit packer info from the Packers tab using &apos;Edit&apos; in the
        Actions dropdown. You can change name, phone number, and other details,
        or delete a packer from the same menu.
        <br />
        Packer unique ID is their email — you can&apos;t have two packers with
        the same email.
      </>
    ),
  },
  {
    q: "How do I assign orders to a driver?",
    a: (
      <>
        You can assign orders in three ways:
        <ul>
          <li>
            <b>From Excel:</b> While uploading, add the driver&apos;s registered
            email so orders map to the right drivers.
          </li>
          <li>
            <b>Single order:</b> On Orders, use &apos;Assign&apos; on a row,
            then pick a driver from the list.
          </li>
          <li>
            <b>Bulk assign:</b> Select multiple orders on Orders, use the
            &apos;Assign&apos; action that appears, then choose a driver.
          </li>
        </ul>
      </>
    ),
  },
];

const Navbar = () => {
  const { plan, billing, openUpgradeModal } = usePlan();
  const [openPlans, setOpenPlans] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [openCall, setOpenCall] = React.useState(false);
  const [openFAQ, setOpenFAQ] = React.useState(false);
  const [openReferrals, setOpenReferrals] = React.useState(false);
  const [openFeedback, setOpenFeedback] = React.useState(false);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard";
  const isMap = location.pathname === "/Map";
  const isRouteOptimization = location.pathname === "/RouteOptimization";
  const isMapSection = isMap || isRouteOptimization;
  const isPaymentReminders = location.pathname === "/PaymentReminders";
  const isPaymentSettings = location.pathname === "/PaymentSettings";
  const queryParams = new URLSearchParams(location.search);
  const limited = queryParams.get("limited");
  // for details
  const userEmail = sessionStorage.getItem("userEmail");
  const role = sessionStorage.getItem("role");
  const [userName, setUserName] = useState("");
  const [organization, setUserOrgaizationName] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [warningSnackbarOpen, setWarningSnackbarOpen] = useState(false);
  const [warningSnackbarMessage, setWarningSnackbarMessage] = useState("");
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState("");

  // Initialize Logger
  const logger = useRef(new Logger(getLoggerBaseURL())).current;

  let permission = {};
  const permissionString = sessionStorage.getItem("permissions");
  if (permissionString) {
    permission = JSON.parse(permissionString);
  } else {
    logger.log("Permission missing", "ERROR");
  }

  const isActive = (path) => {
    return location.pathname === path ? "active-link" : "";
  };

  const navigate = useNavigate();

  const toggleUserDropdown = () => {
    setUserDropdown(!userDropdown);
  };

  const handleClickOpenCall = () => {
    setOpenCall(true);
  };

  const handleCloseCall = () => {
    setOpenCall(false);
  };

  const handleClickOpenFeedback = () => {
    setOpenFeedback(true);
  };

  const handleCloseFeedback = () => {
    setOpenFeedback(false);
  };

  const handleLinkClick = (e) => {
    e.preventDefault();
    setUserDropdown(false);
    setOpen(true);
    logger.log("User clicked on terms or privacy policy link", "INFO");
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickOpenFAQ = () => {
    setOpenFAQ(true);
  };

  const handleCloseFAQ = () => {
    setOpenFAQ(false);
  };

  const handleToggleFAQ = () => {
    if (!openFAQ) {
      setUserDropdown(false);
      handleClickOpenFAQ();
    }
  };

  const handleClickOpenReferrals = () => {
    setOpenReferrals(true);
  };

  const handleCloseReferrals = () => {
    setOpenReferrals(false);
  };

  const handleToggleReferrals = () => {
    if (!openReferrals) {
      setUserDropdown(false);
      handleClickOpenReferrals();
    }
  };

  const handleToggleCall = () => {
    if (!openCall) {
      setUserDropdown(false);
      handleClickOpenCall();
    }
  };

  const handleToggleFeedback = () => {
    if (!openFeedback) {
      setUserDropdown(false);
      handleClickOpenFeedback();
    }
  };

  function homePage() {
    if (limited !== "true") {
      window.location.href = "/dashboard";
    } else {
      window.location.href = "/packerLanding";
    }
  }

  const handleSendFeedback = () => {
    const feedbackContent = document
      .querySelector("#feedbackTextArea")
      .value.trim();

    if (!feedbackContent) {
      setWarningSnackbarMessage("Please enter your feedback before sending.");
      setWarningSnackbarOpen(true);
      return;
    }

    const adminName = userName;
    const adminOrganization = organization;

    const templateParams = {
      message: feedbackContent,
      admin_name: adminName,
      admin_organization: adminOrganization,
    };

    emailjs
      .send(
        "service_x7mnwbc",
        "template_fppddn8",
        templateParams,
        "98r6gU5wGJ6MCrqlC"
      )
      .then((response) => {
        logger.log("Feedback mail sent successfully", "INFO");
        setSnackbarMessage("Thank you for your feedback!");
        setSnackbarOpen(true);
        handleCloseFeedback();
      })
      .catch((error) => {
        logger.log(`Error: ${error.message}`, "ERROR");
        setErrorSnackbarMessage(
          "Failed to send feedback. Please try again later."
        );
        setErrorSnackbarOpen(true);
      });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`/api/getUsersEmail/${userEmail}`);
        if (response.ok) {
          const userData = await response.json();
          setUserName(userData.user.name);
          setUserOrgaizationName(userData.user.organization);
        } else {
          logger.log("Failed to fetch user data for Navbar", "ERROR");
        }
      } catch (error) {
        logger.log(`Error fetching user data: ${error.message}`, "ERROR");
      }
    };

    if (userEmail) {
      fetchUserData();
    }
  }, [userEmail, logger]);

  useEffect(() => {
    setUserDropdown(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      const response = await fetch(`/api/signup/logout/${userEmail}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to log out");
      }

      // If API call is successful, remove drivers and permissions from sessionStorage
      sessionStorage.removeItem("drivers");
      sessionStorage.removeItem("permissions");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("companyName");
      sessionStorage.removeItem("address");
      sessionStorage.removeItem("phoneNo");

      // Navigate to home page
      navigate("/");
    } catch (error) {
      logger.log(`Error during logout: ${error.message}`, "ERROR");
    }
  };

  return (
    <div className="navbar-container">
      <nav>
        <div className="title" onClick={homePage} style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "20px", cursor: "pointer", }}>
          <img src="/zordigo-logo.png" alt="Zordigo Logo" style={{ height: "40px", width: "auto", objectFit: "contain", }} />
          <b style={{ color: "#c1272c", fontSize: "35px", lineHeight: 1, margin: 0, }}>Zordigo</b>
        </div>
        <ul>
          {limited === "true" ? (
            <>
              <li className={`l ${isActive("/getQueue")}`}>Print Requests</li>
              <li className={`l ${isActive("/Packer")}`}>
                <Link to="/packerLanding">Packer</Link>
              </li>
            </>
          ) : (
            <>
              {role !== "packer" && (
                <li className={`l ${isActive("/dashboard")}`}>
                  <Link to="/dashboard">Dashboard</Link>
                </li>
              )}
              {role !== "packer" && (
                <li className={`l ${isActive("/CurrentOrder")} || ${isActive("/AssignedOrder")} || ${isActive("/CompleteOrder")} || ${isActive("/IncompleteOrder")} || ${isActive("/Pending")} || ${isActive("/BillToCollect")}`}>
                  <Link to="/CurrentOrder">Orders</Link>
                </li>
              )}
              {role !== "packer" && (
                <li className={`l ${isActive("/DriverTable")}`}>
                  <Link to="/DriverTable">Drivers</Link>
                </li>
              )}
              {role !== "dispatcher" && role !== "packer" && (
                <li className={`l ${isActive("/Dispatcher")}`}>
                  <Link to="/Dispatcher">Dispatchers</Link>
                </li>
              )}
              {role !== "packer" && (
                <li className={`l ${isActive("/Packer")}`}>
                  <Link to="/Packer">Packers</Link>
                </li>
              )}
              {role !== "packer" && (
                <li className={`l ${isActive("/Map")} || ${isActive("/RouteOptimization")}`}>
                  <Link
                    to="/Map"
                    onClick={(e) => {
                      if (plan?.liveTrackingEnabled === false) {
                        e.preventDefault();
                        openUpgradeModal({
                          code: "FEATURE_LOCKED",
                          message:
                            "Live tracking / Map is not included in your plan. Upgrade to Advance.",
                          upgrade: billing?.upgrade,
                        });
                      }
                    }}
                  >
                    Map
                  </Link>
                </li>
              )}
              {role !== "packer" && (
                <li className={`l ${isActive("/PaymentReminders")} || ${isActive("/PaymentSettings")}`}>
                  <Link to="/PaymentReminders">Payment Reminders</Link>
                </li>
              )}
              {role !== "packer" && (
                <li className={`l ${isActive("/Reports")} || ${isActive("/orderExcel")} || ${isActive("/driver")} || ${isActive("/reportId")} || ${isActive("/summary")} || ${isActive("/account")}`}>
                  <Link to="/Reports">Reports</Link>
                </li>
              )}
              {role === "packer" && (
                <>
                  <li className={`l ${isActive("/packerLanding")}`}>
                    <Link to="/packerLanding">Home</Link>
                  </li>
                  <li className={`l ${isActive("/packerOrders")}`}>
                    <Link to="/packerOrders">Orders</Link>
                  </li>
                  <li className={`l ${isActive("/packerSummaries")}`}>
                    <Link to="/packerSummaries">Summaries</Link>
                  </li>
                  <li className={`l ${isActive("/getQueue")}`}>
                    <Link to="/getQueue">Print Requests</Link>
                  </li>
                </>
              )}
            </>
          )}
          <li className="iconFace">
            <FaRegUserCircle
              onClick={toggleUserDropdown}
              className={`userDropdown-btn ${userDropdown ? "open" : ""}`}
            />
            {limited === "true" ? (
              <>
                {userDropdown && (
                  <div
                    ref={dropdownRef}
                    className={`userDropdown-contentt ${userDropdown ? "show" : ""
                      }`}
                  >
                    <p className="userAndCompanyNamee">
                      {userName && (
                        <p className="userAndCompanyNamee">
                          <b>{userName}</b>
                        </p>
                      )}
                    </p>
                    <p className="userAndCompanyName">
                      {organization && (
                        <p className="userAndCompanyName">{organization}</p>
                      )}
                    </p>
                    <hr className="darkHR" />
                    <li>
                      <Link to="/getQueue">
                        <div className="link-content">
                          <IoMdPrint className="iconDi" />
                          <span className="link-text">Print Requests </span>
                        </div>
                      </Link>
                    </li>
                    <hr className="darkHR" />
                    <li>
                      <Link to="/" onClick={handleLogout}>
                        <div className="link-content">
                          <FiLogOut className="iconDi" />
                          <span className="link-text"> Log Out </span>
                        </div>
                      </Link>
                    </li>
                  </div>
                )}
              </>
            ) : (
              <>
                {userDropdown && (
                  <div
                    ref={dropdownRef}
                    className={`userDropdown-content ${userDropdown ? "show" : ""
                      }`}
                  >
                    <p className="userAndCompanyNamee">
                      {userName && (
                        <p className="userAndCompanyNamee">
                          <b>{userName}</b>
                        </p>
                      )}
                    </p>
                    <p className="userAndCompanyName">
                      {organization && (
                        <p className="userAndCompanyName">{organization}</p>
                      )}
                    </p>
                    <hr className="darkHR" />
                    {role === "admin" ||
                      (role === "dispatcher" &&
                        (permission.orderCreation || permission.orderUpload)) ? (
                      <li>
                        <Link to="/settings">
                          <div className="link-content">
                            <IoSettingsOutline className="iconDi" />
                            <span className="link-text">Settings</span>
                          </div>
                        </Link>
                      </li>
                    ) : null}
                    {role !== "packer" && (
                      <>
                        <li>
                          <Link to="/Ledgers">
                            <div className="link-content">
                              <FaBook className="iconDi" />
                              <span className="link-text">Ledgers </span>
                            </div>
                          </Link>
                        </li>
                        <hr className="darkHR" />
                        <li>
                          <button
                            type="button"
                            className="link-button"
                            onClick={handleToggleReferrals}
                          >
                            <div className="link-content">
                              <FiGift className="iconDi" />
                              <span className="link-text"> Referrals </span>
                            </div>
                          </button>
                        </li>
                        <li>
                          <Link to="/getQueue">
                            <div className="link-content">
                              <IoMdPrint className="iconDi" />
                              <span className="link-text">Print Requests </span>
                            </div>
                          </Link>
                        </li>
                        <hr className="darkHR" />
                        <li>
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => {
                              setUserDropdown(false);
                              setOpenPlans(true);
                            }}
                          >
                            <div className="link-content">
                              <TbCalendarStats className="iconDi" />
                              <span className="link-text">Plans </span>
                            </div>
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            className="link-button"
                            onClick={handleLinkClick}
                          >
                            <div className="link-content">
                              <MdOutlineInstallMobile className="iconDi" />
                              <span className="link-text">Download the app </span>
                            </div>
                          </button>
                        </li>
                        <hr className="darkHR" />
                      </>
                    )}
                    <li>
                      <button
                        type="button"
                        className="link-button"
                        onClick={handleToggleFAQ}
                      >
                        <div className="link-content">
                          <IoMdHelpCircleOutline className="iconDi" />{" "}
                          <span className="link-text">
                            Frequently Asked Questions{" "}
                          </span>
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="link-button"
                        onClick={handleLinkClick}
                      >
                        <div className="link-content">
                          <IoVideocamOutline className="iconDi" />{" "}
                          <span className="link-text">Video Tutorials </span>
                        </div>
                      </button>
                    </li>
                    <hr className="darkHR" />
                    <li>
                      <button
                        type="button"
                        className="link-button"
                        onClick={handleToggleCall}
                      >
                        <div className="link-content">
                          <IoMailOutline className="iconDi" />
                          <span className="link-text"> Talk to Us</span>
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="link-button"
                        onClick={handleLinkClick}
                      >
                        <div className="link-content">
                          <MdOutlineChat className="iconDi" />
                          <span className="link-text"> Chat With Us</span>
                        </div>
                      </button>
                    </li>
                    <hr className="darkHR" />
                    <li>
                      <button
                        type="button"
                        className="link-button"
                        onClick={handleToggleFeedback}
                      >
                        <div className="link-content">
                          <TfiAnnouncement className="iconDi" />
                          <span className="link-text">Send Feedback</span>
                        </div>
                      </button>
                    </li>
                    <li>
                      <Link to="/" onClick={handleLogout}>
                        <div className="link-content">
                          <FiLogOut className="iconDi" />
                          <span className="link-text"> Log Out </span>
                        </div>
                      </Link>
                    </li>
                  </div>
                )}
              </>
            )}
          </li>
        </ul>
      </nav>
      {role !== "packer" && !(isDashboard || isMapSection || isPaymentReminders || isPaymentSettings) && (
        <MovableLiveStatus />
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        className="under-construction-dialog"
        PaperProps={{
          className: "under-construction-paper",
          elevation: 0,
        }}
      >
        <div className="under-construction-header">
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            size="small"
            className="under-construction-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <span className="under-construction-icon" aria-hidden>
            <Construction fontSize="inherit" />
          </span>
          <h2 className="under-construction-heading">Coming Soon</h2>
          <p className="under-construction-sub">
            This section is under development. We’re polishing it and will
            publish updates shortly.
          </p>
        </div>
        <div className="under-construction-actions">
          <Button
            onClick={handleClose}
            variant="contained"
            disableElevation
            className="under-construction-btn"
          >
            Got it
          </Button>
        </div>
      </Dialog>

      <Dialog
        className="referrals-dialog"
        open={openReferrals}
        onClose={handleCloseReferrals}
        PaperProps={{
          className: "referrals-paper",
          elevation: 0,
        }}
      >
        <div className="referrals-header">
          <IconButton
            aria-label="Close"
            onClick={handleCloseReferrals}
            size="small"
            className="referrals-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <span className="referrals-badge" aria-hidden>
            <FiGift />
          </span>
          <h2 className="referrals-heading">Refer &amp; earn</h2>
          <p className="referrals-sub">
            Get <strong>3,000 Zordigo points</strong> when your referred
            customer joins Zordigo and signs up for any of our paid plans.
          </p>
        </div>
        <div className="referrals-body">
          <p className="referrals-share-label">Share on social media</p>
          <div className="referrals-socials">
            {ZORDIGO_SOCIALS.map(({ label, href, Icon }) => (
              <a
                key={label}
                className="referrals-social-btn"
                href={href}
                target={href.startsWith("mailto:") ? undefined : "_blank"}
                rel={
                  href.startsWith("mailto:") ? undefined : "noopener noreferrer"
                }
                aria-label={label}
                title={label}
              >
                <Icon />
              </a>
            ))}
          </div>
        </div>
      </Dialog>

      <Dialog
        className="faq-dialog"
        open={openFAQ}
        onClose={handleCloseFAQ}
        fullWidth
        maxWidth="md"
        PaperProps={{
          className: "faq-paper",
          elevation: 0,
        }}
      >
        <div className="faq-header">
          <IconButton
            aria-label="Close"
            onClick={handleCloseFAQ}
            size="small"
            className="faq-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <h2 className="faq-heading">Frequently Asked Questions</h2>
          <p className="faq-sub">
            Quick answers to common questions about using Zordigo.
          </p>
        </div>
        <div className="faq-body">
          {FAQ_ITEMS.map((item, index) => (
            <Accordion
              key={item.q}
              disableGutters
              elevation={0}
              defaultExpanded={index === 0}
              className="faq-accordion"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className="faq-accordion-summary"
              >
                {item.q}
              </AccordionSummary>
              <AccordionDetails className="faq-accordion-details">
                {item.a}
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </Dialog>

      <Dialog
        open={openCall}
        onClose={handleCloseCall}
        aria-labelledby="talk-to-us-title"
        className="talk-to-us-dialog"
        PaperProps={{
          className: "talk-to-us-paper",
          elevation: 0,
        }}
      >
        <div className="talk-to-us-header">
          <IconButton
            aria-label="Close"
            onClick={handleCloseCall}
            size="small"
            className="talk-to-us-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <h2 id="talk-to-us-title" className="talk-to-us-heading">
            Talk to Us
          </h2>
          <p className="talk-to-us-sub">
            Our team is here to help with anything you need.
          </p>
        </div>
        <div className="talk-to-us-body">
          <a className="talk-to-us-row" href="mailto:info@zordigo.com">
            <span className="talk-to-us-icon" aria-hidden>
              <IoMailOutline />
            </span>
            <span className="talk-to-us-row-text">
              <span className="talk-to-us-row-label">Email</span>
              <span className="talk-to-us-row-value">info@zordigo.com</span>
            </span>
          </a>
          <div className="talk-to-us-section">
            <p className="talk-to-us-section-label">Phone</p>
            <a className="talk-to-us-row" href="tel:+919910206708">
              <span className="talk-to-us-icon" aria-hidden>
                <MdOutlinePhone />
              </span>
              <span className="talk-to-us-row-text">
                <span className="talk-to-us-row-value">+91 99102 06708</span>
              </span>
            </a>
            <a className="talk-to-us-row" href="tel:+919910206748">
              <span className="talk-to-us-icon" aria-hidden>
                <MdOutlinePhone />
              </span>
              <span className="talk-to-us-row-text">
                <span className="talk-to-us-row-value">+91 99102 06748</span>
              </span>
            </a>
          </div>
        </div>
      </Dialog>

      <Dialog
        className="feedback-dialog"
        open={openFeedback}
        onClose={handleCloseFeedback}
        PaperProps={{
          className: "feedback-paper",
          elevation: 0,
        }}
      >
        <div className="feedback-header">
          <IconButton
            aria-label="Close"
            onClick={handleCloseFeedback}
            size="small"
            className="feedback-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <h2 className="feedback-heading">Send Feedback</h2>
          <p className="feedback-sub">
            Tell us about a problem, idea, or anything we can improve. We read
            every message.
          </p>
        </div>
        <div className="feedback-body">
          <label className="feedback-label" htmlFor="feedbackTextArea">
            Your message
          </label>
          <TextField
            id="feedbackTextArea"
            multiline
            minRows={7}
            fullWidth
            placeholder="Hi,

Please share your feedback — bugs, suggestions, or feature requests.

Thanks"
            className="feedback-field"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#fff",
                alignItems: "flex-start",
                "& fieldset": {
                  borderColor: "#e2e8f0",
                },
                "&:hover fieldset": {
                  borderColor: "#cbd5e1",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#07a6ef",
                  borderWidth: "1.5px",
                },
              },
              "& .MuiOutlinedInput-input": {
                fontSize: "0.92rem",
                lineHeight: 1.5,
                color: "#0f172a",
              },
            }}
          />
        </div>
        <div className="feedback-actions">
          <Button
            variant="outlined"
            onClick={handleCloseFeedback}
            className="feedback-btn-secondary"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSendFeedback}
            className="feedback-btn-primary"
          >
            Send feedback
          </Button>
        </div>
        <Snackbar
          open={warningSnackbarOpen}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
        >
          <MuiAlert severity="warning" elevation={6} variant="filled">
            {warningSnackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {errorSnackbarMessage}
        </MuiAlert>
      </Snackbar>

      <PlansDialog open={openPlans} onClose={() => setOpenPlans(false)} />
    </div>
  );
};
export default Navbar;
